import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  Heartbeat, Warning, CheckCircle, ShieldWarning, ArrowRight,
  MagnifyingGlass, FunnelSimple, CurrencyGbp
} from "@phosphor-icons/react";
import { toast } from "sonner";

const HEALTH_CONFIG = {
  green: {
    label: "On Track",
    dot: "bg-emerald-500",
    bg: "border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10",
    text: "text-emerald-700 dark:text-emerald-400",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400",
  },
  amber: {
    label: "At Risk",
    dot: "bg-amber-500",
    bg: "border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10",
    text: "text-amber-700 dark:text-amber-400",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
  },
  red: {
    label: "Critical",
    dot: "bg-red-500",
    bg: "border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10",
    text: "text-red-700 dark:text-red-400",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
  },
  completed: {
    label: "Completed",
    dot: "bg-blue-500",
    bg: "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10",
    text: "text-blue-700 dark:text-blue-400",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
  },
};

export default function PortfolioHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState("all");

  useEffect(() => {
    API.get("/portfolio/health")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load portfolio health"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );

  const summary = data?.summary || {};
  const projects = (data?.projects || []).filter(p => {
    if (healthFilter !== "all" && p.health !== healthFilter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.reference.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalActive = summary.green + summary.amber + summary.red;

  return (
    <div data-testid="portfolio-health-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Portfolio Health</h1>
        <p className="text-sm text-muted-foreground mt-1">Traffic-light overview of all active projects</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="health-summary-cards">
        <div className="border border-border rounded-md p-4 text-center" data-testid="summary-total">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Total Active</p>
          <p className="text-3xl font-bold font-[Chivo]">{totalActive}</p>
        </div>
        <button onClick={() => setHealthFilter(healthFilter === "green" ? "all" : "green")} className={`border rounded-md p-4 text-center transition-all ${healthFilter === "green" ? "ring-2 ring-emerald-500" : "border-border hover:border-emerald-300"}`} data-testid="summary-green">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">On Track</p>
          <p className="text-3xl font-bold font-[Chivo] text-emerald-600 dark:text-emerald-400">{summary.green}</p>
        </button>
        <button onClick={() => setHealthFilter(healthFilter === "amber" ? "all" : "amber")} className={`border rounded-md p-4 text-center transition-all ${healthFilter === "amber" ? "ring-2 ring-amber-500" : "border-border hover:border-amber-300"}`} data-testid="summary-amber">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">At Risk</p>
          <p className="text-3xl font-bold font-[Chivo] text-amber-600 dark:text-amber-400">{summary.amber}</p>
        </button>
        <button onClick={() => setHealthFilter(healthFilter === "red" ? "all" : "red")} className={`border rounded-md p-4 text-center transition-all ${healthFilter === "red" ? "ring-2 ring-red-500" : "border-border hover:border-red-300"}`} data-testid="summary-red">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Critical</p>
          <p className="text-3xl font-bold font-[Chivo] text-red-600 dark:text-red-400">{summary.red}</p>
        </button>
        <button onClick={() => setHealthFilter(healthFilter === "completed" ? "all" : "completed")} className={`border rounded-md p-4 text-center transition-all ${healthFilter === "completed" ? "ring-2 ring-blue-500" : "border-border hover:border-blue-300"}`} data-testid="summary-completed">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Completed</p>
          <p className="text-3xl font-bold font-[Chivo] text-blue-600 dark:text-blue-400">{summary.completed}</p>
        </button>
      </div>

      {/* Health Distribution Bar */}
      {totalActive > 0 && (
        <div className="border border-border rounded-md p-4 mb-6" data-testid="health-distribution-bar">
          <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-muted">
            {summary.green > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${(summary.green / totalActive) * 100}%` }} title={`On Track: ${summary.green}`} />}
            {summary.amber > 0 && <div className="bg-amber-500 transition-all" style={{ width: `${(summary.amber / totalActive) * 100}%` }} title={`At Risk: ${summary.amber}`} />}
            {summary.red > 0 && <div className="bg-red-500 transition-all" style={{ width: `${(summary.red / totalActive) * 100}%` }} title={`Critical: ${summary.red}`} />}
          </div>
          <div className="flex gap-6 mt-2 text-[11px]">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> On Track ({summary.green})</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> At Risk ({summary.amber})</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Critical ({summary.red})</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="health-filters">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects..." className="pl-9 h-9 text-sm" data-testid="health-search" />
        </div>
        <Select value={healthFilter} onValueChange={setHealthFilter}>
          <SelectTrigger className="w-40 h-9 text-sm" data-testid="health-filter-select">
            <FunnelSimple size={14} className="mr-1" /><SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="green">On Track</SelectItem>
            <SelectItem value="amber">At Risk</SelectItem>
            <SelectItem value="red">Critical</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        {(search || healthFilter !== "all") && (
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setSearch(""); setHealthFilter("all"); }} data-testid="clear-health-filters">
            Clear filters
          </Button>
        )}
      </div>

      {/* Project Cards */}
      {projects.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <Heartbeat size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No projects match the current filter.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {projects.map(p => {
            const cfg = HEALTH_CONFIG[p.health] || HEALTH_CONFIG.green;
            return (
              <Link key={p.id} to={`/projects/${p.id}`} className={`block border rounded-md p-4 transition-all hover:shadow-md group ${cfg.bg}`} data-testid={`health-card-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.reference} | {p.property_address || "No address"}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.badge}`}>
                    <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                    {cfg.label}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-2 mb-3">
                  <Progress value={p.progress} className="h-1.5 flex-1" />
                  <span className="text-[10px] font-semibold text-muted-foreground shrink-0">{p.progress}%</span>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-4 gap-2 text-[10px] text-muted-foreground mb-3">
                  <div>
                    <span className="block font-medium text-foreground">{p.task_count}</span>
                    Tasks
                  </div>
                  <div>
                    <span className="block font-medium text-foreground">{p.completed_tasks}</span>
                    Done
                  </div>
                  <div>
                    <span className={`block font-medium ${p.overdue_count > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>{p.overdue_count}</span>
                    Overdue
                  </div>
                  <div>
                    <span className={`block font-medium ${p.expired_compliance > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>{p.expired_compliance}</span>
                    Expired Docs
                  </div>
                </div>

                {/* Budget */}
                {p.budget > 0 && (
                  <div className="pt-2 border-t border-border/50">
                    <div className="flex items-center justify-between text-[10px] mb-1">
                      <span className="text-muted-foreground">Budget: <span className="font-medium text-foreground">{"\u00A3"}{p.budget.toLocaleString()}</span></span>
                      <span className={`font-semibold ${p.budget_utilisation > 100 ? "text-red-600 dark:text-red-400" : p.budget_utilisation > 80 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>
                        {p.budget_utilisation}% used
                      </span>
                    </div>
                    <div className="h-1 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${p.budget_utilisation > 100 ? "bg-red-500" : p.budget_utilisation > 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, p.budget_utilisation)}%` }} />
                    </div>
                  </div>
                )}

                {/* Reasons */}
                {(p.red_reasons?.length > 0 || p.amber_reasons?.length > 0) && (
                  <div className="mt-3 pt-2 border-t border-border/50 space-y-1">
                    {(p.red_reasons || []).map((r, i) => (
                      <div key={`r-${i}`} className="flex items-center gap-1.5 text-[10px] text-red-600 dark:text-red-400">
                        <Warning size={10} weight="fill" /> {r}
                      </div>
                    ))}
                    {(p.amber_reasons || []).map((r, i) => (
                      <div key={`a-${i}`} className="flex items-center gap-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                        <ShieldWarning size={10} weight="fill" /> {r}
                      </div>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
